import { useEffect, useState, useRef, useCallback } from 'react';
import { getOfflinePatientRecord } from '../../services/offlineService';
import { subscribeToPatientProgress, subscribeToPatientErrors } from '../../services/patientService';

const DEFAULT_STRUCTURE = 'Pelvis';
const DEFAULT_DIFFUSION_SPACING = '0';

/**
 * @param {string} patientId
 * @param {string | null} offlinePatientId
 */
export function usePatientStatus(patientId, offlinePatientId) {
  const [patientName, setPatientName] = useState('');
  const [segmentationDone, setSegmentationDone] = useState(false);
  const [segmentationValidated, setSegmentationValidated] = useState(true);
  const [modelExported, setModelExported] = useState(false);
  const [nervesRecognition, setNervesRecognition] = useState(false);
  const [segmentedStructure, setSegmentedStructure] = useState(DEFAULT_STRUCTURE);
  const [diffusionSpacing, setDiffusionSpacing] = useState(DEFAULT_DIFFUSION_SPACING);
  const [patientStatusLoaded, setPatientStatusLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [patientNameError, setPatientNameError] = useState(false);

  const segmentationStatusUnsubscribeRef = useRef(/** @type {null | (() => void)} */ (null));
  const errorUnsubscribeRef = useRef(/** @type {null | (() => void)} */ (null));
  const cancelledRef = useRef(false);

  const resetStatusState = useCallback(() => {
    setSegmentationDone(false);
    setSegmentationValidated(true);
    setPatientName('');
    setPatientNameError(false);
    setNervesRecognition(false);
    setSegmentedStructure(DEFAULT_STRUCTURE);
    setDiffusionSpacing(DEFAULT_DIFFUSION_SPACING);
    setModelExported(false);
    setPatientStatusLoaded(false);
    setIsError(false);
  }, []);

  const unsubscribeError = useCallback(() => {
    if (!errorUnsubscribeRef.current) return;
    errorUnsubscribeRef.current();
    errorUnsubscribeRef.current = null;
  }, []);

  const unsubscribeSegmentationStatus = useCallback(() => {
    if (!segmentationStatusUnsubscribeRef.current) return;
    segmentationStatusUnsubscribeRef.current();
    segmentationStatusUnsubscribeRef.current = null;
  }, []);

  useEffect(() => {
    cancelledRef.current = false;

    if (!patientId) {
      resetStatusState();
      unsubscribeSegmentationStatus();
      unsubscribeError();
      return;
    }

    resetStatusState();

    if (offlinePatientId) {
      getOfflinePatientRecord(patientId)
        .then((record) => {
          if (cancelledRef.current) return;
          setPatientName(record?.displayName || '');
          setSegmentationDone(true);
          setSegmentationValidated(true);
          setModelExported(true);
          setPatientStatusLoaded(true);
        })
        .catch(() => {
          if (cancelledRef.current) return;
          setSegmentationDone(true);
          setSegmentationValidated(true);
          setModelExported(true);
          setPatientStatusLoaded(true);
        });
      unsubscribeError();
      return;
    }

    if (segmentationStatusUnsubscribeRef.current) {
      segmentationStatusUnsubscribeRef.current();
    }
    segmentationStatusUnsubscribeRef.current = subscribeToPatientProgress(
      patientId,
      (patientInfo) => {
        if (cancelledRef.current) return;
        if (!patientInfo) {
          resetStatusState();
          setPatientStatusLoaded(true);
          return;
        }
        setSegmentationDone(patientInfo.seg_done);
        setSegmentationValidated(patientInfo.seg_validated);
        setPatientName(patientInfo.display_name || patientInfo.displayName || '');
        setPatientNameError(patientInfo._nameDecryptFailed ?? false);
        setNervesRecognition(patientInfo.nerves ?? false);
        setSegmentedStructure(patientInfo.structure ?? DEFAULT_STRUCTURE);
        setDiffusionSpacing(patientInfo.diff_spacing ?? DEFAULT_DIFFUSION_SPACING);
        setModelExported(patientInfo.model_exported);
        setPatientStatusLoaded(true);
      },
      async (err) => {
        console.warn('usePatientStatus: Firestore error, using offline fallback', err);
        try {
          const record = await getOfflinePatientRecord(patientId);
          if (cancelledRef.current) return;
          setPatientName(record?.displayName || '');
        } catch {
          if (cancelledRef.current) return;
          setPatientName('');
        }
        setSegmentationDone(true);
        setSegmentationValidated(true);
        setNervesRecognition(false);
        setSegmentedStructure(DEFAULT_STRUCTURE);
        setDiffusionSpacing(DEFAULT_DIFFUSION_SPACING);
        setModelExported(true);
        setPatientStatusLoaded(true);
      },
    );

    unsubscribeError();
    errorUnsubscribeRef.current = subscribeToPatientErrors(
      patientId,
      (hasError, errorType) => {
        setIsError(hasError);
      },
      () => {
        setIsError(false);
      },
    );

    return () => {
      cancelledRef.current = true;
      unsubscribeSegmentationStatus();
      unsubscribeError();
    };
  }, [patientId, offlinePatientId, resetStatusState, unsubscribeError, unsubscribeSegmentationStatus]);

  return {
    patientName,
    patientNameError,
    segmentationDone,
    segmentationValidated,
    modelExported,
    nervesRecognition,
    segmentedStructure,
    diffusionSpacing,
    patientStatusLoaded,
    isError,
  };
}
